'use client';

import { useEffect, useState, useCallback, useRef, useSyncExternalStore } from 'react';
import { api, getApiUrl } from '@/lib/api';
import { MapPin, Menu } from 'lucide-react';
import { useOptionalDashboardShell } from '@/components/dashboard/dashboard-shell-context';
import { SystemHealthDot } from '@/components/dashboard/SystemHealthDot';
import { SYSTEM_HEALTH_LABEL, SITE_FILTER, siteCount } from '@/lib/copy';
import Link from 'next/link';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { formatClockWithZone } from '@/lib/dateFormat';

interface TopBarProps {
  title: string;
  subtitle?: string;
  status?: 'nominal' | 'degraded' | 'critical';
  wsConnected?: boolean;
  /** Page-specific control rendered between the title and the status cluster
   *  (the COP puts its drone search here). Pages that pass nothing are
   *  unchanged. */
  actions?: React.ReactNode;
  /**
   * The alert indication, at the head of the status cluster.
   *
   * A separate slot from `actions` on purpose: this is system state, and it
   * belongs with the subsystem dots and the health word, not among the page's
   * own controls. Only the COP passes one today, because the alert list is
   * loaded by the COP's own `useCopAlerts`; every other route renders the
   * cluster exactly as before.
   *
   * Whatever is passed MUST have a height that does not depend on its content.
   * This header is a sibling of the COP's map column, and `DroneMap` turns any
   * change in the column's box into a real MapLibre canvas resize — a header
   * that grows when an alert arrives would move the drone the operator is
   * tracking, which is the whole reason the alert surface left the map.
   */
  alerts?: React.ReactNode;
  /**
   * The 44 px variant, for a route whose content is the whole screen.
   *
   * The COP's tactical shell asks for it: the map is full bleed under this
   * header, so every pixel the header does not need is map. Nothing is removed
   * except height and the title's second line - the site picker, the health
   * dots, the clock and the status word all stay, because an operator reads
   * those while flying.
   *
   * Applies at `lg` and up only. Below it the header is exactly as it was, which
   * is the same rule the rest of the tactical shell follows: no layout below
   * `lg` changes until someone has answered which display this is for.
   *
   * Deliberately a prop rather than a route check: the header does not know
   * which routes want to be dense, and a component that reads the pathname to
   * decide how tall it is cannot be tested at one size.
   */
  dense?: boolean;
  /**
   * The name of the site the picture is filtered to, or null for the whole
   * fleet.
   *
   * Given rather than read from the URL here: `site_id` is the COP's parameter
   * and this header is on every route, and a header that reads the query string
   * to decide what it says cannot be rendered in a test at all.
   *
   * When set, the picker names the site instead of counting them, and carries
   * the way back out. That is the whole of the SiteFilterBanner that used to sit
   * in a row of its own above the map - a row that appeared and disappeared IN
   * the map's flex column, so setting a filter resized the MapLibre canvas and
   * moved every drone on screen.
   */
  siteFilterLabel?: string | null;
  /**
   * Chrome floating over the page's LEFT edge, in CSS px.
   *
   * On the COP the navigation rail is an overlay spanning the full height, so it
   * covers this header's left end - the nav menu button, the title and the site
   * picker - unless the header stands clear of it. Zero on every other route,
   * where the rail is a flex sibling and takes its own width out of the row.
   */
  leftInsetPx?: number;
}

interface SiteOption {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

interface HealthResponse {
  status: string;
  services: {
    postgresql: string;
    redis: string;
    mqtt: string;
  };
}

type ServiceState = 'connected' | 'disconnected' | 'unknown';

interface SubsystemHealth {
  db: ServiceState;
  redis: ServiceState;
  mqtt: ServiceState;
}

const HEALTH_POLL_INTERVAL = 30_000;

/**
 * The wall clock, in the org's own timezone (Settings → Organization), with the
 * zone always named beside it.
 *
 * It read UTC unconditionally until 2026-09-09, while the rest of the dashboard
 * had already followed `useOrgTimezone()` for a while - so on an org set to
 * Asia/Jerusalem the clock disagreed with every timestamp under it by three
 * hours, which is worse than either choice made consistently. A UTC org sees
 * exactly what it saw before.
 *
 * The subscription is unchanged: a 1 Hz tick, and the store's snapshot is the
 * formatted string, so the zone is re-read every second and a DST change lands
 * on its own without a reload.
 */
/** Hoisted out of the component: `useSyncExternalStore` re-subscribes whenever
 *  this identity changes, and an arrow allocated per render meant a
 *  clearInterval/setInterval pair on every telemetry frame the COP re-rendered
 *  on. It closes over nothing, so there is no reason for it to be inline. */
function subscribeToSecond(onStoreChange: () => void): () => void {
  const id = setInterval(onStoreChange, 1000);
  return () => clearInterval(id);
}

function OrgClock() {
  const timezone = useOrgTimezone();
  const time = useSyncExternalStore(
    subscribeToSecond,
    () => formatClockWithZone(Date.now(), timezone),
    // Unreachable in practice - `(dashboard)/layout.tsx` renders
    // `<DashboardInitializing />` until auth resolves, so neither the SSR pass
    // nor hydration ever reaches this clock. Kept zone-free because a snapshot
    // that named a zone would be asserting one the server does not know.
    () => '--:--:--',
  );
  return (
    <span className="font-mono text-base leading-none text-foreground tracking-wider tabular-nums">
      {time}
    </span>
  );
}

export function TopBar({
  title,
  subtitle,
  status = 'nominal',
  wsConnected,
  actions,
  alerts,
  dense = false,
  siteFilterLabel = null,
  leftInsetPx = 0,
}: TopBarProps) {
  const shell = useOptionalDashboardShell();
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [showSites, setShowSites] = useState(false);
  const showNavMenu = shell?.breakpointMode === 'drawer';

  const [subsystem, setSubsystem] = useState<SubsystemHealth>({
    db: 'unknown',
    redis: 'unknown',
    mqtt: 'unknown',
  });
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${getApiUrl()}/health`, { signal: AbortSignal.timeout(5000) });
      const data: HealthResponse = await res.json();
      setSubsystem({
        db: data.services.postgresql === 'connected' ? 'connected' : 'disconnected',
        redis: data.services.redis === 'connected' ? 'connected' : 'disconnected',
        mqtt: data.services.mqtt === 'connected' ? 'connected' : 'disconnected',
      });
    } catch {
      setSubsystem({ db: 'disconnected', redis: 'disconnected', mqtt: 'disconnected' });
    }
  }, []);

  useEffect(() => {
    void fetchHealth();
    pollRef.current = setInterval(() => void fetchHealth(), HEALTH_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchHealth]);

  useEffect(() => {
    api.get<{ data: SiteOption[] }>('/api/sites').then((r) => setSites(r.data)).catch(() => {});
  }, []);

  // WebSocket runs on the same server as the API. If health check passes, WS is available.
  // Use explicit prop if provided (COP page), otherwise infer from health check.
  const wsState: ServiceState =
    wsConnected !== undefined
      ? (wsConnected ? 'connected' : 'disconnected')
      : (subsystem.db === 'connected' ? 'connected' : 'unknown');

  /**
   * Nothing has answered yet, on this poll or the first one.
   *
   * Held apart from `overallDegraded` because it is not a fault: the health
   * endpoint has a 5 s timeout and every dependency starts `unknown`. But the
   * dot is amber for `unknown` (D24), so without this the corner said amber
   * beside the word NOMINAL for the first seconds of every load - a caution
   * colour next to a green word, which is a fault hunt that finds nothing, or
   * an operator who learns to ignore the dot.
   */
  const healthUnknown =
    subsystem.db === 'unknown' || subsystem.redis === 'unknown' || subsystem.mqtt === 'unknown';

  const overallDegraded =
    status === 'degraded' ||
    status === 'critical' ||
    subsystem.db === 'disconnected' ||
    subsystem.redis === 'disconnected' ||
    subsystem.mqtt === 'disconnected' ||
    wsState === 'disconnected';

  const overallLabel = status === 'critical'
    ? SYSTEM_HEALTH_LABEL.critical
    : overallDegraded
      ? SYSTEM_HEALTH_LABEL.degraded
      : healthUnknown
        ? SYSTEM_HEALTH_LABEL.unknown
        : SYSTEM_HEALTH_LABEL.nominal;

  const overallTextColor = status === 'critical'
    ? 'text-status-red-fg'
    : overallDegraded
      ? 'text-status-amber-fg'
      : healthUnknown
        ? 'text-status-amber-fg'
        : 'text-status-green-fg';

  return (
    <>
      <header
        data-dense={dense ? 'true' : 'false'}
        className={`flex items-center justify-between gap-3 border-b border-border bg-background min-w-0 px-4 sm:px-6 py-3 ${
          dense ? 'lg:px-4 lg:py-1.5' : ''
        }`}
        // The padding classes stay and are only added to, so with nothing
        // floating this keeps its rem-based gutters under text zoom.
        style={leftInsetPx > 0 ? { paddingLeft: `calc(1rem + ${leftInsetPx}px)` } : undefined}
      >
        {/* Left: nav + title + site picker */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {showNavMenu && shell && (
            <button
              type="button"
              onClick={() => shell.toggleDrawer()}
              className="shrink-0 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/10 border border-border"
              aria-label="Open navigation menu"
            >
              <Menu className="size-5" strokeWidth={1.5} />
            </button>
          )}
          <div className="min-w-0">
            <h1
              className={`leading-tight font-semibold text-foreground truncate text-[20px] ${
                dense ? 'lg:text-[14px]' : ''
              }`}
            >
              {title}
            </h1>
            {subtitle && (
              <p className={`text-[11px] text-muted-foreground truncate ${dense ? 'lg:hidden' : ''}`}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Rendered when there is a filter to report EVEN IF the site list
              failed to load. The list is a fly-to convenience; the filter label
              and its way out are the operator's only signal that the picture is
              not the whole fleet, and TopBar's own `/api/sites` call swallows
              its errors. Gating all three on that fetch left a filtered COP with
              nothing saying so and no control to clear it - which is the exact
              thing D26 says had to survive the fold. */}
          {(sites.length > 0 || siteFilterLabel) && (
            <div className="relative">
              <button type="button"
                onClick={() => setShowSites(!showSites)}
                // Tinted while a filter is on, because "the picture you are
                // looking at is not the whole fleet" has to be legible without
                // reading the label. The banner said it with the same
                // `bg-primary/10`.
                title={siteFilterLabel ? SITE_FILTER.filteredTo(siteFilterLabel) : undefined}
                aria-label={siteFilterLabel ? SITE_FILTER.filteredTo(siteFilterLabel) : undefined}
                className={
                  siteFilterLabel
                    ? 'flex max-w-[180px] items-center gap-1.5 rounded-md border border-subtle bg-primary/10 px-2.5 py-1 text-[11px] text-foreground transition-colors hover:bg-accent/10'
                    : 'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors border border-border'
                }
              >
                <MapPin className="size-3 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{siteFilterLabel ?? siteCount(sites.length)}</span>
              </button>
              {showSites && (
                <div className="absolute top-full left-0 mt-1 z-50 glass-card rounded-lg py-1 min-w-[160px]">
                  {siteFilterLabel && (
                    // The banner's CLEAR link, which was the only way back to the
                    // whole fleet. A real navigation, because the filter is a
                    // query parameter and not component state.
                    <Link
                      href="/"
                      onClick={() => setShowSites(false)}
                      className="block border-b divider px-3 py-1.5 text-sm text-primary transition-colors hover:bg-accent/10"
                    >
                      {SITE_FILTER.clear}
                    </Link>
                  )}
                  {sites.map((site) => (
                    <button type="button"
                      key={site.id}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('flyToSite', { detail: { lat: site.latitude, lng: site.longitude } }));
                        setShowSites(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent/10 hover:text-foreground transition-colors"
                    >
                      {site.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rendered bare, not in a wrapper: the control owns its own width and
            responsive visibility, so a hidden one leaves no empty flex item
            (and no stray gap) in the header behind it. */}
        {actions}

        {/* Right: the alert bell + subsystem dots + clock + status label.
            shrink-0: the health word and the clock are never the thing that gives
            way; the title on the left truncates instead. */}
        <div className="flex shrink-0 items-center gap-4">
          {alerts}

          {/* One dot, coloured by the worst of the four (amendment E). They were
              four things the eye had to process in the corner of a tactical
              display, for state an operator never acts on. The four are one
              hover or one press away, and the dot's accessible name still
              carries all of them. */}
          <SystemHealthDot
            states={{
              db: subsystem.db,
              cache: subsystem.redis,
              mqtt: subsystem.mqtt,
              updates: wsState,
            }}
          />

          <OrgClock />

          <span className={`text-[11px] font-medium tracking-wider uppercase ${overallTextColor}`}>
            {overallLabel}
          </span>
        </div>
      </header>
    </>
  );
}
